'use client';

import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { Check, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { FormInput } from '@/components/forms/form-input';
import { FormSelect } from '@/components/forms/form-select';
import { createDeviceSchema, type DeviceFormValues } from '../schemas';
import {
  useCreateDeviceMutation,
  useUpdateDeviceMutation
} from '../services/devices.mutations';
import type { Device, DeviceType, DiscoveredDevice } from '../types';

interface DeviceFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  device?: Device | null;
  prefill?: DiscoveredDevice | null;
}

const EMPTY_DEFAULTS: DeviceFormValues = {
  name: '',
  scanner_identifier: null,
  device_type: 'scanner',
  serial_number: '',
  ip_address: '',
  status: 'online',
  location: '',
  software_version: '',
  max_daily_capacity: undefined
};

function buildPrefillValues(prefill: DiscoveredDevice): DeviceFormValues {
  return {
    ...EMPTY_DEFAULTS,
    name: prefill.name,
    scanner_identifier: prefill.scanner_identifier,
    device_type: prefill.device_type,
    serial_number: prefill.serial_number ?? '',
    ip_address: prefill.ip_address ?? '',
    software_version: prefill.software_version ?? ''
  };
}

export function DeviceFormDialog({
  open,
  onOpenChange,
  device,
  prefill
}: DeviceFormDialogProps) {
  const t = useTranslations('devices');
  const tv = useTranslations('validation');
  const isEditing = !!device;

  const schema = useMemo(() => createDeviceSchema(tv), [tv]);

  const form = useForm<DeviceFormValues>({
    resolver: zodResolver(schema) as any,
    defaultValues: EMPTY_DEFAULTS
  });

  const createMutation = useCreateDeviceMutation();
  const updateMutation = useUpdateDeviceMutation();
  const isPending = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (!open) return;
    if (device) {
      form.reset({
        name: device.name,
        scanner_identifier: device.scanner_identifier,
        device_type: device.device_type ?? 'scanner',
        serial_number: device.serial_number ?? '',
        ip_address: device.ip_address ?? '',
        status: device.status,
        location: device.location ?? '',
        software_version: device.software_version ?? '',
        max_daily_capacity: device.max_daily_capacity ?? undefined
      });
    } else if (prefill) {
      form.reset(buildPrefillValues(prefill));
    } else {
      form.reset(EMPTY_DEFAULTS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, device, prefill]);

  async function onSubmit(values: DeviceFormValues) {
    const { scanner_identifier, ...rest } = values;
    const basePayload = {
      ...rest,
      serial_number: rest.serial_number || null,
      ip_address: rest.ip_address || null,
      location: rest.location || null,
      software_version: rest.software_version || null,
      max_daily_capacity: rest.max_daily_capacity ?? null
    };

    try {
      if (isEditing && device) {
        // scanner_identifier is immutable post-registration; the backend
        // strips it from update requests, so don't bother sending it.
        await updateMutation.mutateAsync({ id: device.id, data: basePayload });
      } else {
        await createMutation.mutateAsync({
          ...basePayload,
          scanner_identifier: scanner_identifier ?? null
        });
      }
      onOpenChange(false);
    } catch {
      // handled by global MutationCache
    }
  }

  const statusOptions = useMemo(
    () => [
      { value: 'online', label: t('statusOnline') },
      { value: 'offline', label: t('statusOffline') },
      { value: 'maintenance', label: t('statusMaintenance') }
    ],
    [t]
  );

  const typeOptions = useMemo<{ value: DeviceType; label: string }[]>(
    () => [
      { value: 'scanner', label: t('type_scanner') },
      { value: 'printer', label: t('type_printer') },
      { value: 'barcode_scanner', label: t('type_barcode_scanner') }
    ],
    [t]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[520px]'>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('editDevice') : t('addDevice')}
          </DialogTitle>
        </DialogHeader>

        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
          <div className='grid gap-4 py-4'>
            <FormSelect
              control={form.control}
              name='device_type'
              label={t('typeLabel')}
              options={typeOptions}
            />

            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <FormInput
                control={form.control}
                name='name'
                label={t('nameLabel')}
                placeholder={t('namePlaceholder')}
                required
              />
              <FormInput
                control={form.control}
                name='serial_number'
                label={t('serialNumberLabel')}
                placeholder={t('serialNumberPlaceholder')}
              />
            </div>

            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <FormInput
                control={form.control}
                name='ip_address'
                label={t('ipAddressLabel')}
                placeholder={t('ipAddressPlaceholder')}
              />
              <FormSelect
                control={form.control}
                name='status'
                label={t('statusLabel')}
                options={statusOptions}
              />
            </div>

            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <FormInput
                control={form.control}
                name='location'
                label={t('locationLabel')}
                placeholder={t('locationPlaceholder')}
              />
              <FormInput
                control={form.control}
                name='software_version'
                label={t('softwareVersionLabel')}
                placeholder={t('softwareVersionPlaceholder')}
              />
            </div>

            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <FormInput
                control={form.control}
                name='max_daily_capacity'
                label={t('maxDailyCapacityLabel')}
                placeholder={t('maxDailyCapacityPlaceholder')}
                type='number'
              />
            </div>
          </div>
        </Form>

        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
          >
            {t('cancel')}
          </Button>
          <Button
            type='submit'
            disabled={isPending}
            onClick={form.handleSubmit(onSubmit)}
          >
            {isPending ? (
              <Loader2 className='me-2 h-4 w-4 animate-spin' />
            ) : (
              <Check className='me-2 h-4 w-4' />
            )}
            {isEditing ? t('saveChanges') : t('create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
